import { METER_STR, SIC, SOC, STRATIFY, THIRTY_CM_STR } from 'constants/projects';

import {
  MeterCoreFormType,
  MeterCorePayloadType,
  RegistryConfig,
  SamplePlanFormV2Type,
  SoilHealthFormType,
  ThirtyCmCoreFormType,
  WetAggregateStabilityFormType,
} from 'store/projects/types';

export const getSamplePlanFormValues = (config: RegistryConfig): Partial<SamplePlanFormV2Type> => ({
  project_type: config.land_types[0],
  exclusion_layers: config.exclusion_layers,
  method: STRATIFY,
  isMeterCore: config.depths.includes(METER_STR),
  isThirtyCmCore: config.depths.includes(THIRTY_CM_STR),
  isSoilHealth: true,
  isWetAggregateStability: true,
  tree_shrub: 85,
  slope: 58,
  mc_dict: getMeterCoreFormValues(config.meter_core),
  step_probe_dict: getThirtyCmCoreFormValues(config.thirty_cm_core),
  sh_dict: getSoilHealthFormValues(config.soil_health),
  was_dict: getWetAggregateStabilityFormValues(config.wet_aggregate_stability),
});

export const getMeterCoreFormValues = (
  config: RegistryConfig['meter_core'] | undefined,
): MeterCoreFormType => ({
  lab: config?.labs?.[0] || '',
  sic: config?.sic.default ? SIC : SOC,
  density: config?.densities?.[0] || 0,
  texture_density: config?.texture_densities?.[0] || '',
  is_split_core: config?.is_split_core.default || false,
  splits: config?.splits.length ? String(config?.splits?.[0].label) : '',
  frozen_storage: config?.frozen_storage.default || false,
  field_requirement: config?.min_one_per_field.default || false,
  add_texture_ph: config?.add_texture_and_ph.default || false,
});

export const getThirtyCmCoreFormValues = (
  config: RegistryConfig['thirty_cm_core'] | undefined,
): ThirtyCmCoreFormType => ({
  lab: config?.labs?.[0] || '',
  sic: config?.sic.default ? SIC : SOC,
  density: config?.densities?.[0] || 0,
  texture_density: config?.texture_densities?.[0] || '',
  is_split_core: config?.is_split_core.default || false,
  field_requirement: config?.min_one_per_field.default || false,
  add_texture_ph: config?.add_texture_and_ph.default || false,
  reduced_bd: config?.reduced_bd.default || false,
});

export const getSoilHealthFormValues = (
  config: RegistryConfig['soil_health'] | undefined,
): SoilHealthFormType => ({
  lab: config?.labs?.[0] || '',
  density: String(config?.densities?.[0].value) || '',
});

export const getWetAggregateStabilityFormValues = (
  config: RegistryConfig['wet_aggregate_stability'] | undefined,
): WetAggregateStabilityFormType => ({
  lab: config?.labs?.[0] || '',
  density: String(config?.densities?.[0].value) || '',
});

export const getDensityOptionsFromConfig = (densities: number[]) => {
  return densities.map((value) => ({
    value: String(value),
    label: `1 per ${value} acres`,
  }));
};

export const getMeterCorePayload = (
  isMeterCore: boolean,
  meterCoreFormValues: MeterCoreFormType,
  possibleSplits: { value: number[]; label: string }[],
): MeterCorePayloadType | null => {
  if (!isMeterCore) return null;
  const splits =
    meterCoreFormValues.splits &&
    possibleSplits.find((split) => split.label === meterCoreFormValues.splits)?.value;

  return {
    ...meterCoreFormValues,
    lab: meterCoreFormValues.lab,
    splits: splits || null,
    sic: meterCoreFormValues.sic === SIC, // Either SIC is selected or its the defualt -- boolean in Demeter
    density: Number(meterCoreFormValues.density),
    texture_density: meterCoreFormValues.add_texture_ph
      ? meterCoreFormValues.texture_density
      : null,
  };
};

export const getThirtyCmCorePayload = (
  isThirtyCmCore: boolean,
  thirtyCmCoreFormValues: ThirtyCmCoreFormType,
) =>
  isThirtyCmCore
    ? {
        ...thirtyCmCoreFormValues,
        lab: thirtyCmCoreFormValues.lab,
        sic: thirtyCmCoreFormValues.sic === SIC, // Either SIC is selected or its the defualt -- boolean in Demeter
        density: Number(thirtyCmCoreFormValues.density),
        texture_density: thirtyCmCoreFormValues.add_texture_ph
          ? thirtyCmCoreFormValues.texture_density
          : null,
        splits: thirtyCmCoreFormValues.is_split_core ? [15] : null,
        reduced_bd: thirtyCmCoreFormValues.reduced_bd,
      }
    : null;

export const getSoilHealthPayload = (
  isSoilHealth: boolean,
  soilHealthFormValues: SoilHealthFormType,
) =>
  isSoilHealth
    ? {
        lab: soilHealthFormValues.lab,
        density: Number(soilHealthFormValues.density),
      }
    : null;

export const getWetAggregateStabilityPayload = (
  isWetAggregateStability: boolean,
  wetAggregateStabilityFormValues: WetAggregateStabilityFormType,
) =>
  isWetAggregateStability
    ? {
        lab: wetAggregateStabilityFormValues.lab,
        density: Number(wetAggregateStabilityFormValues.density),
      }
    : null;
